import React, { Fragment, forwardRef } from "react";
import classnames from "classnames";
import parse, { domToReact } from "html-react-parser";

// Components
import DecisionTreeNode from "../decision-tree-node/DecisionTreeNode";
import ContactUsStandardSolution from "../contact-us-standard-solution/ContactUsStandardSolution";
import ContactUsStandardExpandedSolution from "../contact-us-standard-expanded-solution/ContactUsStandardExpandedSolution";
import ContactUsReboundSolution from "../contact-us-rebound-solution/ContactUsReboundSolution";
import ContactUsNonElectricalSolution from "../contact-us-non-electrical-solution/ContactUsNonElectricalSolution";
import ContactUsManufacturerSolution from "../contact-us-manufacturer-solution/ContactUsManufacturerSolution";
import ContactUsRefundsSolution from "../contact-us-refunds-solution/ContactUsRefundsSolution";
import ContactUsDeliverySolution from "../contact-us-delivery-solution/ContactUsDeliverySolution";
import ContactUsProblemWithProductGuarantees from "../contact-us-problems-with-product-guarantees/ContactUsProblemWithProductGuarantees";
import ContactUsProductStockSolution from "../contact-us-product-stock-solution/ContactUsProductStockSolution";
import ContactUsProductStockOtherSolution from "../contact-us-product-stock-other-solution/ContactUsProductStockOtherSolution";
import ContactUsProductStockToolSolution from "../contact-us-product-stock-tool-solution/ContactUsProductStockToolSolution";
import ContactUsProductStockPriceMatch from "../contact-us-product-stock-price-match/ContactUsProductStockPriceMatch";
import ContactUsHelpOrderSolution from "../contact-us-help-order-solution/ContactUsHelpOrderSolution";
import ContactUsRepairUpdateElectricalItem from "../contact-us-repair-update-electrical-item/ContactUsRepairUpdateElectricalItem";
import ContactUsProductStockCheckInStoreSolution from "../contact-us-product-stock-check-in-store-solution/ContactUsProductStockCheckInStoreSolution";

// Constants
import templateNames from "../../constants/templateNames";

// Styles
import styles from "./solution.module.scss";

const parseOptions = {
  replace: ({ type, name: tagName, attribs, children }) =>
    tagName === "br"
      ? undefined
      : type === "tag" &&
        React.createElement(
          tagName,
          Object.keys(attribs)
            .filter(attribKey => {
              if (["style", "class"].includes(attribKey)) return false;
              if (attribKey === "href" && attribs[attribKey] === "#") return false;
              return true;
            })
            .reduce(
              (acc, attribKey) => ({
                ...acc,
                [attribKey]: attribs[attribKey],
              }),
              {},
            ),
          domToReact(children, parseOptions),
        ),
};

export function DefaultSolution({ solution: { id, title, fields }, className, titleClassName }) {
  return (
    <article className={classnames(styles.solution, className)} data-solution-id={id}>
      <h2 className={titleClassName}>{title}</h2>
      {fields
        .filter(({ content }) => !!content)
        .map(({ content, name }) => (
          <Fragment key={name}>{parse(content, parseOptions)}</Fragment>
        ))}
    </article>
  );
}

const getComponent = (type, templateName) => {
  if (type === "custom") {
    switch (templateName) {
      case templateNames.CONTACT_US_DECISION_TREE_NODE:
        return DecisionTreeNode;
      case templateNames.CONTACT_US_STANDARD_SOLUTION:
        return ContactUsStandardSolution;
      case templateNames.CONTACT_US_STANDARD_EXPANDED_SOLUTION:
        return ContactUsStandardExpandedSolution;
      case templateNames.CONTACT_US_NON_ELECTRICAL_SOLUTION:
        return ContactUsNonElectricalSolution;
      case templateNames.CONTACT_US_MANUFACTURER_SOLUTION:
        return ContactUsManufacturerSolution;
      case templateNames.CONTACT_US_REFUNDS_SOLUTION:
        return ContactUsRefundsSolution;
      case templateNames.CONTACT_US_DELIVERY_SOLUTION:
        return ContactUsDeliverySolution;
      case templateNames.CONTACT_US_PROBLEMS_WITH_PRODUCT_GUARANTEES_AND_PROTECT_PLUS:
        return ContactUsProblemWithProductGuarantees;
      case templateNames.CONTACT_US_PRODUCT_STOCK_SOLUTION:
        return ContactUsProductStockSolution;
      case templateNames.CONTACT_US_PRODUCT_STOCK_OTHER_SOLUTION:
        return ContactUsProductStockOtherSolution;
      case templateNames.CONTACT_US_PRODUCT_STOCK_TOOL_SOLUTION:
        return ContactUsProductStockToolSolution;
      case templateNames.CONTACT_US_PRODUCT_STOCK_PRICE_MATCH:
        return ContactUsProductStockPriceMatch;
      case templateNames.CONTACT_US_HELP_ORDER_SOLUTION:
        return ContactUsHelpOrderSolution;
      case templateNames.CONTACT_US_REPAIR_UPDATE_ELECTRICAL_ITEM_SOLUTION:
        return ContactUsRepairUpdateElectricalItem;
      case templateNames.CONTACT_US_REBOUND_SOLUTION:
        return ContactUsReboundSolution;
      case templateNames.CONTACT_US_PRODUCT_STOCK_CHECK_IN_STORE_SOLUTION:
        return ContactUsProductStockCheckInStoreSolution;
      default:
        return DefaultSolution;
    }
  }
  return DefaultSolution;
};

const Solution = forwardRef(function Solution(
  { experiences = [], solution, decisionTreeDisableSolutions = [], ...rest },
  ref,
) {
  const Component = getComponent(solution.type, solution.templateName);

  const getActiveDiagnosticResponses = diagnosticResponses =>
    diagnosticResponses.filter(dRes => !decisionTreeDisableSolutions.includes(dRes.id));

  return (
    <Component
      ref={ref}
      experiences={experiences}
      solution={{
        ...solution,
        diagnosticResponses: getActiveDiagnosticResponses(solution.diagnosticResponses),
      }}
      {...rest}
    />
  );
});

export default Solution;
